import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Link, useHistory } from 'react-router-dom'
import { Redirect } from 'react-router'

import ApproveEstimateModal from './components/ApproveEstimateModal'
import DeclineEstimateModal from './components/DeclineEstimateModal'
import ReviceEstimateModal from './components/ReviceEstimateModal'
import ContentLayout from '../../components/ContentLayout/ContentLayout'
import Button from '../../components/Button/Button'
import InfoLine from '../../components/CardInfo/components/InfoLine'
import CustomDivider from '../../components/CardInfo/components/CustomDivider'
import CardInfo from '../../components/CardInfo/CardInfo'
import CellLink from '../../components/Table/components/CellLink'
import WarningCard from '../CreateRequestStepper.js/components/WarningCard'
import AppRoutes from '../../routes/appRoutes'
import StatusBadge from '../../components/StatusBadge/StatusBadge'

import { tagHotjarRecording } from '../../helpers/util'
import useRequest from '../../hooks/useRequest'
import useAuth from '../../hooks/useAuth'
import useAlert from '../../hooks/useAlert'
import useScreen from '../../hooks/useScreen'
import useGlobalLoading from '../../hooks/useGlobalLoading'
import { DownloadIcon } from '@heroicons/react/outline'
import clsx from 'clsx'

const ViewServiceEstimatePage = props => {
    const { match } = props

    const [estimate, setEstimate] = useState([])
    const [estimateId, setEstimateId] = useState('')
    const [latestVersion, setLatestVersion] = useState('')
    const [error, setError] = useState('')
    const [openApproveModal, setOpenApproveModal] = useState(false)
    const [openDeclineModal, setOpenDeclineModal] = useState(false)
    const [openReviseModal, setOpenReviseModal] = useState(false)
    const [showFooter, setShowFooter] = useState(false)
    const [showEstimateHistory, setShowEstimateHistory] = useState(false)

    const {
        handleGetEstimateInfo,
        handleEstimateApproval,
        changeNeedsUpdate,
    } = useRequest()
    const { user } = useAuth()
    const { handleChangeAlert, handleChangeOpen } = useAlert()
    const { isMobile } = useScreen()
    const { changeLoading } = useGlobalLoading()
    const history = useHistory()

    const onGetEstimateInfoSuccess = response => {
        let estimateInfo = response?.data?.estimate
        setEstimate(estimateInfo)
        setShowFooter(estimateInfo?.showFooter)
        setShowEstimateHistory(estimateInfo?.showHistory)
        if (!estimateInfo?.isLastestVersion) {
            setLatestVersion(estimateInfo?.latestEstimateId)
        }
        changeLoading(false)
    }

    const onGetEstimateInfoError = data => {
        tagHotjarRecording('Get estimate data error')
        let message = data?.response?.data?.message
        setError(message)
        changeLoading(false)
        console.log(error)
    }

    useEffect(() => {
        let estimateId = match?.params?.estimateId
        setEstimateId(estimateId)
        changeLoading(true)
        handleGetEstimateInfo(
            estimateId,
            onGetEstimateInfoSuccess,
            onGetEstimateInfoError,
        )
    }, [])

    const onApprovalEstimateError = error => {
        console.log(error)
        const {
            message = 'Please check your internet connection and try again.',
        } = error?.response?.data ?? {}
        setError(message)
        changeLoading(false)
    }

    const estimateSuccess = () => {
        setError('')
        changeLoading(false)
        changeNeedsUpdate({ dashboard: true, request: true })
        setShowFooter(false)
        handleGetEstimateInfo(
            estimateId,
            onGetEstimateInfoSuccess,
            onGetEstimateInfoError,
        )
    }

    const onDeclineEstimateSuccess = () => {
        handleChangeAlert({
            title: 'Decline Successfully Sent',
            message:
                'Thank you for providing the details. Our team will contact you within 1 - 2 business days.',
            textOk: 'View Work Order',
            textCancel: 'Go to Dashboard',
            onCancel: () => history?.push(AppRoutes.dashboard()),
            onDone: () => {
                history?.push(
                    AppRoutes.viewServiceRequest(estimate?.workOrderId),
                )
                handleChangeOpen(false)
            },
            type: 'success',
        })
        estimateSuccess()
    }

    const declineEstimate = data => {
        changeLoading(true)
        setOpenDeclineModal(false)
        let declineData = {}
        declineData.estimateId = estimateId
        declineData.action = 'Decline'
        declineData.poNumber = null
        declineData.customerResponse = data?.declineText
        declineData.signature = null
        declineData.userId = user?.id

        handleEstimateApproval(
            declineData,
            onDeclineEstimateSuccess,
            onApprovalEstimateError,
        )
    }

    const onReviseEstimateSuccess = () => {
        handleChangeAlert({
            title: 'Revision Successfully Sent',
            message:
                'Thank you for providing the details. Our team will contact you within 1 - 2 business days.',
            textOk: 'View Work Order',
            textCancel: 'Go to Dashboard',
            onCancel: () => history?.push(AppRoutes.dashboard()),
            onDone: () => {
                history?.push(
                    AppRoutes.viewServiceRequest(estimate?.workOrderId),
                )
                handleChangeOpen(false)
            },
            type: 'success',
        })
        estimateSuccess()
    }

    const reviseEstimate = data => {
        changeLoading(true)
        setOpenReviseModal(false)
        let reviseData = {}
        reviseData.estimateId = estimateId
        reviseData.action = 'Revise'
        reviseData.poNumber = null
        reviseData.customerResponse = data?.revisedText
        reviseData.signature = null
        reviseData.userId = user?.id

        handleEstimateApproval(
            reviseData,
            onReviseEstimateSuccess,
            onApprovalEstimateError,
        )
    }

    const onApproveEstimateSuccess = () => {
        handleChangeAlert({
            title: 'Approval Successfully Sent',
            message:
                'Thank you for providing the details. Our team will contact you within 1 - 2 business days.',
            textOk: 'View Work Order',
            textCancel: 'Go to Dashboard',
            onCancel: () => history?.push(AppRoutes.dashboard()),
            onDone: () => {
                history?.push(
                    AppRoutes.viewServiceRequest(estimate?.workOrderId),
                )
                handleChangeOpen(false)
            },
            type: 'success',
        })
        estimateSuccess()
    }

    const approveEstimate = data => {
        changeLoading(true)
        setOpenApproveModal(false)
        let approveData = {}
        approveData.estimateId = estimateId
        approveData.action = 'Approve'
        approveData.poNumber = data?.poNumber
        approveData.customerResponse = null
        approveData.signature = data?.signature
        approveData.userId = user?.id

        handleEstimateApproval(
            approveData,
            onApproveEstimateSuccess,
            onApprovalEstimateError,
        )
    }

    const renderHeader = () => {
        return (
            <div className="w-full h-auto flex flex-col items-start justify-start p-4 bg-white border-b border-grayLines lg:gap-4 gap-2">
                <p
                    role="button"
                    onClick={() => {
                        history?.goBack()
                    }}
                    className="text-textDarkGray text-sm font-medium"
                >
                    {'< Go Back'}
                </p>
                <p className="text-black90 font-semibold lg:text-lg text-2xl">
                    {!isMobile && 'Service Estimate Details: '}
                    {estimate?.name}
                </p>
            </div>
        )
    }

    const renderEstimateDetails = () => {
        return (
            <div className="w-full h-auto flex flex-col items-start justify-start lg:py-2 py-0">
                <InfoLine
                    label="Associated Work Order"
                    value={
                        estimate?.workOrderId ? (
                            <CellLink
                                key="linkToWO"
                                text={estimate?.workOrderName}
                                to={AppRoutes.viewServiceRequest(
                                    estimate?.workOrderId,
                                )}
                            />
                        ) : (
                            '-'
                        )
                    }
                    longLabel
                />
                <CustomDivider />
                <InfoLine
                    label="Estimate Status"
                    value={
                        estimate?.status ? (
                            <StatusBadge
                                displayClass={estimate?.status}
                                extraBadgeClass="-ml-3"
                            />
                        ) : (
                            '-'
                        )
                    }
                    longLabel
                />
                <CustomDivider />
                <InfoLine
                    label="Valid Until"
                    value={estimate?.prettyValidUntil || '-'}
                    prompText="This estimate will be canceled after this expiration date"
                    longLabel
                />
                <CustomDivider />
                <InfoLine
                    label="Tech Estimate Description"
                    value={estimate?.techEstimateDescription || '-'}
                    longLabel
                    prompText="Technician's diagnostic notes"
                />
                <CustomDivider />
                <InfoLine
                    label="Initial Description"
                    value={estimate?.initialDescription || '-'}
                    prompText="Description of issue reported by Customer"
                    longLabel
                    isLast
                />
            </div>
        )
    }

    const renderEstimateCharges = () => {
        return (
            <div className="w-full h-auto flex flex-col items-start justify-start lg:py-2 py-0">
                <InfoLine
                    label="Labor"
                    value={
                        estimate?.laborCost ? '$' + estimate?.laborCost : '-'
                    }
                    longLabel
                />
                <CustomDivider />
                <InfoLine
                    label="Parts"
                    value={
                        estimate?.partsCost ? '$' + estimate?.partsCost : '-'
                    }
                    longLabel
                />
                <CustomDivider />
                <InfoLine
                    label="Total"
                    value={
                        estimate?.totalCost ? '$' + estimate?.totalCost : '-'
                    }
                    longLabel
                    extraLineClass="font-extrabold"
                    isLast
                />
            </div>
        )
    }

    const renderEstimateHistory = () => {
        return (
            <div className="w-full h-auto flex flex-col items-start justify-start lg:py-2 py-0">
                {estimate?.estimateHistory.map((pastEstimate, index) => {
                    const isLast = index == estimate.estimateHistory.length - 1
                    return (
                        <div
                            key={pastEstimate?.quoteId}
                            className="w-full h-auto"
                        >
                            <InfoLine
                                key={pastEstimate?.quoteId}
                                label={pastEstimate?.quoteNumber || '-'}
                                value={
                                    pastEstimate?.validUntil
                                        ? renderEstimateDocument(
                                              pastEstimate?.quoteId,
                                              pastEstimate?.prettyValidUntil,
                                              pastEstimate?.summaryReportExists,
                                          )
                                        : '-'
                                }
                                longLabel
                            />
                            {!isLast && <CustomDivider />}
                        </div>
                    )
                })}
            </div>
        )
    }

    const renderEstimateDocument = (
        estimateId,
        estimateValidUntil,
        summaryReportExists,
    ) => (
        <div className="w-full flex place-content-between">
            <Link
                to={'/getEstimateReport?estimateId=' + estimateId}
                target="_blank"
                className={clsx(
                    summaryReportExists
                        ? 'cursor-pointer text-links hover:underline'
                        : 'pointer-events-none',
                )}
            >
                EstimateDocument.pdf
            </Link>
            <div className="text-textDarkGray">{estimateValidUntil}</div>
        </div>
    )

    const renderContentInfo = () => {
        return (
            <div className="lg:w-220 w-full m-auto h-auto flex flex-col items-start justify-start gap-6 lg:mt-12 mt-0 lg:p-0 p-4 lg:mb-0 mb-12">
                <CardInfo
                    title="Service Estimate Information"
                    subTitle="Find the details of the estimate below"
                    content={renderEstimateDetails()}
                    isOverflowVisible
                />

                <CardInfo
                    title="Estimate Charges"
                    subTitle="Taxes, fees and freight not included"
                    content={renderEstimateCharges()}
                    headerActions={[renderDownloadButton()]}
                />
                {showEstimateHistory && (
                    <CardInfo
                        title="Estimate History"
                        content={renderEstimateHistory()}
                    />
                )}
            </div>
        )
    }

    const renderDownloadButton = () => {
        const reportExists = estimate?.summaryReportExists
        if (reportExists) {
            return (
                <Link
                    to={'/getEstimateReport?estimateId=' + estimateId}
                    target="_blank"
                    key="downloadButton"
                    className="cursor-pointer"
                >
                    <Button
                        variant="primary"
                        text="Download Estimate"
                        className={clsx(isMobile ? 'w-full' : 'w-auto')}
                        icon={
                            <DownloadIcon className="text-yellow stroke-current stroke-2 w-6 h-6 mr-2" />
                        }
                    />
                </Link>
            )
        }

        return (
            <div className="relative flex flex-col items-center justify-center group">
                <Button
                    variant="primary"
                    text="Download Estimate"
                    className={clsx(
                        'border border-grayLines',
                        isMobile ? 'w-full' : 'w-auto',
                    )}
                    icon={
                        <DownloadIcon className="text-disable stroke-current stroke-2 w-6 h-6 mr-2" />
                    }
                    disabled={!reportExists}
                />
                <div className="absolute bottom-0 flex flex-col items-center hidden mb-14 group-hover:flex">
                    <span className="relative inline-block  w-54 z-10 p-2 leading-none text-white font-normal whitespace-normal bg-black50 shadow-lg rounded-lg text-center text-xs">
                        Report is not yet available.
                        <br />
                        Check back shortly to download.
                    </span>
                    <div className="w-3 h-3 -mt-2 transform rotate-45 bg-black50"></div>
                </div>
            </div>
        )
    }

    const renderFooter = () => {
        return (
            <div className="w-full mt-2 px-6 py-3.5 sticky bottom-0 left-0 bg-white border-t border-grayLines flex items-center justify-center gap-2">
                <WarningCard error={error} />
                <Button
                    type="button"
                    variant="success"
                    onClick={() => {
                        setOpenApproveModal(true)
                    }}
                    text="Approve"
                />
                <Button
                    type="button"
                    variant="secondary"
                    onClick={() => {
                        setOpenReviseModal(true)
                    }}
                    text="Request Revision"
                />
                <Button
                    type="button"
                    variant="error"
                    onClick={() => {
                        setOpenDeclineModal(true)
                    }}
                    text="Decline"
                />
            </div>
        )
    }

    return (
        <ContentLayout isRelative>
            {latestVersion ? (
                <Redirect to={AppRoutes.viewEstimateRequest(latestVersion)} />
            ) : null}
            {renderHeader()}
            {renderContentInfo()}
            {/* Spacer */}
            <div className="md:flex-grow md:h-full md:block hidden" />
            {showFooter && renderFooter()}
            <ApproveEstimateModal
                isOpen={openApproveModal}
                setIsOpen={setOpenApproveModal}
                onContinue={approveData => approveEstimate(approveData)}
            />
            <DeclineEstimateModal
                isOpen={openDeclineModal}
                setIsOpen={setOpenDeclineModal}
                onContinue={declineData => declineEstimate(declineData)}
            />
            <ReviceEstimateModal
                isOpen={openReviseModal}
                setIsOpen={setOpenReviseModal}
                onContinue={revisedData => reviseEstimate(revisedData)}
            />
        </ContentLayout>
    )
}

ViewServiceEstimatePage.propTypes = {
    match: PropTypes.shape({
        params: PropTypes.shape({
            estimateId: PropTypes.string,
        }),
    }),
    location: PropTypes.shape({
        state: PropTypes.shape({
            source: PropTypes.string,
        }),
    }),
}
export default ViewServiceEstimatePage
